import moment from "moment";

import { documentTypes } from "./types";
import { removeAccents } from "./utils";
import { getPropertyAddress } from "./verifier";

export const WORKLIST_WINDOWS = [7, 30, 60, 90];
export const DEFAULT_WORKLIST_WINDOW_DAYS = 30;

/** Agency Documents-tab types only (excludes lease / extension / etc.). */
const AGENCY_DOCUMENT_TYPE_IDS = new Set(documentTypes.map((entry) => entry.id));

export function isAgencyDocumentType(type) {
	return AGENCY_DOCUMENT_TYPE_IDS.has(type);
}

function startOfToday(now = moment()) {
	return moment(now).startOf("day");
}

export function parseDocumentDate(value) {
	if (!value) return null;
	const parsed = moment(value, [moment.ISO_8601, "YYYY-MM-DD", "DD-MM-YYYY"], true);
	if (parsed.isValid()) return parsed.startOf("day");
	const fallback = moment(value);
	return fallback.isValid() ? fallback.startOf("day") : null;
}

export function daysUntilExpiry(expirationDate, now = moment()) {
	const expiry = parseDocumentDate(expirationDate);
	if (!expiry) return null;
	return expiry.diff(startOfToday(now), "days");
}

/**
 * Classify a document relative to today and the expiring window.
 * - expired: date before today
 * - expiring: today through today+windowDays inclusive
 * - ok: after the window
 */
export function classifyDocumentExpiry(expirationDate, windowDays = DEFAULT_WORKLIST_WINDOW_DAYS, now = moment()) {
	const days = daysUntilExpiry(expirationDate, now);
	if (days == null) return null;
	if (days < 0) return "expired";
	if (days <= Number(windowDays)) return "expiring";
	return "ok";
}

export function flattenAgencyDocuments(properties = []) {
	const rows = [];
	(properties || []).forEach((property) => {
		const address = getPropertyAddress(property);
		const owner = property?.owner?.name || "";
		(property?.documents || []).forEach((document) => {
			if (!document) return;
			rows.push({
				id: document.id,
				propertyId: property.id,
				owner,
				address,
				type: document.type,
				name: document.name || "",
				number: document.number || "",
				startingDate: document.startingDate || "",
				expirationDate: document.expirationDate || "",
				addedDate: document.addedDate || "",
				active: Boolean(document.active),
				document,
			});
		});
	});
	return rows;
}

export function filterAgencyDocuments(
	rows = [],
	{ status = "all", windowDays = DEFAULT_WORKLIST_WINDOW_DAYS, type = "", activeOnly = true, query = "" } = {},
	now = moment(),
) {
	const normalizedQuery = removeAccents(String(query || "").trim().toLowerCase());

	return (rows || []).filter((row) => {
		if (activeOnly && !row.active) return false;
		if (type) {
			if (row.type !== type) return false;
		} else if (!isAgencyDocumentType(row.type)) {
			// "All" matches the Documents catalog only — not lease/extension paperwork.
			return false;
		}

		const expiryStatus = classifyDocumentExpiry(row.expirationDate, windowDays, now);
		if (!expiryStatus) return false;

		if (status === "expired" && expiryStatus !== "expired") return false;
		if (status === "expiring" && expiryStatus !== "expiring") return false;
		if (status === "all" && expiryStatus === "ok") return false;

		if (normalizedQuery) {
			const owner = removeAccents(String(row.owner || "").toLowerCase());
			const address = removeAccents(String(row.address || "").toLowerCase());
			if (!owner.includes(normalizedQuery) && !address.includes(normalizedQuery)) return false;
		}

		return true;
	});
}

export function buildWorklist(rows = [], filters = {}, now = moment()) {
	const filtered = filterAgencyDocuments(rows, filters, now).map((row) => {
		const days = daysUntilExpiry(row.expirationDate, now);
		const status = classifyDocumentExpiry(row.expirationDate, filters.windowDays ?? DEFAULT_WORKLIST_WINDOW_DAYS, now);
		return {
			...row,
			daysUntilExpiry: days,
			expiryStatus: status,
		};
	});

	filtered.sort((a, b) => {
		const aTime = parseDocumentDate(a.expirationDate)?.valueOf() ?? Number.POSITIVE_INFINITY;
		const bTime = parseDocumentDate(b.expirationDate)?.valueOf() ?? Number.POSITIVE_INFINITY;
		if (aTime !== bTime) return aTime - bTime;
		return String(a.owner || "").localeCompare(String(b.owner || ""), "ro", { sensitivity: "base" });
	});

	const expiredCount = filtered.filter((row) => row.expiryStatus === "expired").length;
	const expiringCount = filtered.filter((row) => row.expiryStatus === "expiring").length;

	return {
		rows: filtered,
		total: filtered.length,
		expiredCount,
		expiringCount,
	};
}

export function worklistToCsv(rows = []) {
	const header = ["expirationDate", "daysUntilExpiry", "type", "owner", "address", "number", "status", "propertyId"];
	const escape = (value) => JSON.stringify(value == null ? "" : String(value));
	const lines = rows.map((row) =>
		[
			row.expirationDate || "",
			row.daysUntilExpiry ?? "",
			row.type || "",
			row.owner || "",
			row.address || "",
			row.number || "",
			row.expiryStatus || "",
			row.propertyId || "",
		]
			.map(escape)
			.join(","),
	);
	return [header.join(","), ...lines].join("\r\n");
}

export function downloadCsv(csvText, filename) {
	const blob = new Blob([csvText], { type: "text/csv" });
	const url = window.URL.createObjectURL(blob);
	const anchor = document.createElement("a");
	anchor.setAttribute("hidden", "");
	anchor.setAttribute("href", url);
	anchor.setAttribute("download", filename);
	document.body.appendChild(anchor);
	anchor.click();
	document.body.removeChild(anchor);
	window.URL.revokeObjectURL(url);
}
